/**
 * Hala AI Analytics Dashboard
 * Comprehensive analytics for AI performance monitoring
 */

import { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Brain, TrendingUp, TrendingDown, AlertTriangle, CheckCircle,
  XCircle, AlertCircle, Target, Zap, BarChart3, PieChart,
  RefreshCw, Settings, Play, Pause, Clock, Activity,
  ArrowUp, ArrowDown, Minus
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('hala_admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const COLORS = {
  primary: '#1B3A6B',
  primaryLight: '#2A4A7A',
  gold: '#C5A572',
  success: '#10B981',
  successLight: '#D1FAE5',
  warning: '#F59E0B',
  warningLight: '#FEF3C7',
  error: '#EF4444',
  errorLight: '#FEE2E2',
  info: '#3B82F6',
  infoLight: '#DBEAFE',
  white: '#FFFFFF',
  bg: '#F8FAFC',
  bgDark: '#F1F5F9',
  border: '#E2E8F0',
  text: '#1E293B',
  textMuted: '#64748B',
  textLight: '#94A3B8',
};

const ERROR_REASON_LABELS = {
  intent_wrong: 'Intent خطأ',
  need_wrong: 'Need خطأ',
  service_wrong: 'Service خطأ',
  question_inappropriate: 'سؤال غير مناسب',
  missing_context: 'سياق مفقود',
};

// ═══════════════════════════════════════════════════════════════════════════════
// STAT CARD COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

function StatCard({ title, value, subtitle, icon: Icon, color, trend, trendValue }) {
  return (
    <div style={{
      backgroundColor: COLORS.white,
      borderRadius: '16px',
      padding: '20px',
      border: `1px solid ${COLORS.border}`,
      position: 'relative',
      overflow: 'hidden',
    }}>
      <div style={{
        position: 'absolute',
        top: '-20px',
        right: '-20px',
        width: '100px',
        height: '100px',
        borderRadius: '50%',
        backgroundColor: `${color}10`,
      }} />
      
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '16px' }}>
        <div style={{
          width: '48px',
          height: '48px',
          borderRadius: '12px',
          backgroundColor: `${color}15`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <Icon size={24} color={color} />
        </div>
        
        {trend && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            padding: '4px 8px',
            borderRadius: '6px',
            backgroundColor: trend === 'up' ? COLORS.successLight : trend === 'down' ? COLORS.errorLight : COLORS.bgDark,
            color: trend === 'up' ? COLORS.success : trend === 'down' ? COLORS.error : COLORS.textMuted,
            fontSize: '12px',
            fontWeight: 500,
          }}>
            {trend === 'up' && <ArrowUp size={14} />}
            {trend === 'down' && <ArrowDown size={14} />}
            {trend === 'neutral' && <Minus size={14} />}
            {trendValue}
          </div>
        )}
      </div>
      
      <p style={{ fontSize: '36px', fontWeight: 700, color: COLORS.text, margin: '0 0 4px' }}>
        {value}
      </p>
      <p style={{ fontSize: '14px', color: COLORS.textMuted, margin: 0 }}>{title}</p>
      {subtitle && (
        <p style={{ fontSize: '12px', color: COLORS.textLight, margin: '4px 0 0' }}>{subtitle}</p>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// PROGRESS BAR COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

function ProgressBar({ label, value, total, color }) {
  const percentage = total > 0 ? (value / total * 100) : 0;
  return (
    <div style={{ marginBottom: '12px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
        <span style={{ fontSize: '13px', color: COLORS.text }}>{label}</span>
        <span style={{ fontSize: '13px', fontWeight: 600, color: color }}>{value}</span>
      </div>
      <div style={{
        height: '8px',
        backgroundColor: COLORS.bgDark,
        borderRadius: '4px',
        overflow: 'hidden',
      }}>
        <div style={{
          height: '100%',
          width: `${percentage}%`,
          backgroundColor: color,
          borderRadius: '4px',
          transition: 'width 0.5s ease',
        }} />
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// AUTO LEARNING PANEL
// ═══════════════════════════════════════════════════════════════════════════════

function AutoLearningPanel({ config, onUpdate, onRun, loading }) {
  const [localConfig, setLocalConfig] = useState(config);
  
  useEffect(() => {
    setLocalConfig(config);
  }, [config]);
  
  const handleToggle = async (key) => {
    const newValue = !localConfig[key];
    setLocalConfig({ ...localConfig, [key]: newValue });
    await onUpdate({ [key]: newValue });
  };
  
  return (
    <div style={{
      backgroundColor: COLORS.white,
      borderRadius: '16px',
      padding: '24px',
      border: `1px solid ${COLORS.border}`,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '10px',
            backgroundColor: localConfig?.enabled ? COLORS.successLight : COLORS.bgDark,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <Zap size={20} color={localConfig?.enabled ? COLORS.success : COLORS.textMuted} />
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: COLORS.text }}>
              التعلم التلقائي
            </h3>
            <p style={{ margin: '2px 0 0', fontSize: '12px', color: COLORS.textMuted }}>
              {localConfig?.enabled ? 'مُفعّل' : 'معطّل'}
            </p>
          </div>
        </div>
        
        <button
          onClick={() => handleToggle('enabled')}
          style={{
            padding: '8px 16px',
            borderRadius: '8px',
            border: 'none',
            backgroundColor: localConfig?.enabled ? COLORS.error : COLORS.success,
            color: COLORS.white,
            fontSize: '13px',
            fontWeight: 500,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          {localConfig?.enabled ? <Pause size={16} /> : <Play size={16} />}
          {localConfig?.enabled ? 'إيقاف' : 'تفعيل'}
        </button>
      </div>
      
      {/* Settings */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(2, 1fr)', 
        gap: '16px',
        marginBottom: '20px',
      }}>
        <label style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '12px',
          backgroundColor: COLORS.bg,
          borderRadius: '8px',
          cursor: 'pointer',
        }}>
          <input
            type="checkbox"
            checked={localConfig?.auto_approve_correct || false}
            onChange={() => handleToggle('auto_approve_correct')}
            style={{ width: '18px', height: '18px' }}
          />
          <div>
            <p style={{ margin: 0, fontSize: '13px', fontWeight: 500, color: COLORS.text }}>
              اعتماد "صحيحة" تلقائياً
            </p>
            <p style={{ margin: '2px 0 0', fontSize: '11px', color: COLORS.textMuted }}>
              اعتماد التقييمات الصحيحة بدون مراجعة
            </p>
          </div>
        </label>
        
        <label style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '12px',
          backgroundColor: COLORS.bg,
          borderRadius: '8px',
          cursor: 'pointer',
        }}>
          <input
            type="checkbox"
            checked={localConfig?.auto_apply_after_approve || false}
            onChange={() => handleToggle('auto_apply_after_approve')}
            style={{ width: '18px', height: '18px' }}
          />
          <div>
            <p style={{ margin: 0, fontSize: '13px', fontWeight: 500, color: COLORS.text }}>
              تطبيق بعد الاعتماد تلقائياً
            </p>
            <p style={{ margin: '2px 0 0', fontSize: '11px', color: COLORS.textMuted }}>
              إنشاء قواعد تعلم من المعتمد
            </p>
          </div>
        </label>
      </div>
      
      {/* Scheduling Section */}
      <div style={{
        backgroundColor: COLORS.infoLight,
        padding: '16px',
        borderRadius: '12px',
        marginBottom: '20px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <Clock size={16} color={COLORS.info} />
          <p style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: COLORS.info }}>
            جدولة التشغيل التلقائي
          </p>
        </div>
        
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={localConfig?.schedule_enabled || false}
              onChange={() => handleToggle('schedule_enabled')}
              style={{ width: '16px', height: '16px' }}
            />
            <span style={{ fontSize: '13px', color: COLORS.text }}>تفعيل الجدولة</span>
          </label>
          
          {localConfig?.schedule_enabled && (
            <>
              <select
                value={localConfig?.schedule_interval || 'daily'}
                onChange={async (e) => {
                  const newConfig = { ...localConfig, schedule_interval: e.target.value };
                  setLocalConfig(newConfig);
                  await onUpdate({ schedule_interval: e.target.value });
                }}
                style={{
                  padding: '6px 10px',
                  borderRadius: '6px',
                  border: `1px solid ${COLORS.border}`,
                  fontSize: '12px',
                  fontFamily: 'inherit',
                }}
              >
                <option value="hourly">كل ساعة</option>
                <option value="daily">يومياً</option>
                <option value="weekly">أسبوعياً</option>
              </select>
              
              {localConfig?.schedule_interval !== 'hourly' && (
                <input
                  type="time"
                  value={localConfig?.schedule_time || '02:00'}
                  onChange={async (e) => {
                    const newConfig = { ...localConfig, schedule_time: e.target.value };
                    setLocalConfig(newConfig);
                    await onUpdate({ schedule_time: e.target.value });
                  }}
                  style={{
                    padding: '6px 10px',
                    borderRadius: '6px',
                    border: `1px solid ${COLORS.border}`,
                    fontSize: '12px',
                    fontFamily: 'inherit',
                  }}
                />
              )}
              
              {localConfig?.next_scheduled_run && (
                <span style={{ fontSize: '11px', color: COLORS.textMuted }}>
                  التشغيل القادم: {new Date(localConfig.next_scheduled_run).toLocaleString('ar-SA')}
                </span>
              )}
            </>
          )}
        </div>
      </div>
      
      {/* Stats */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 16px',
        backgroundColor: COLORS.bg,
        borderRadius: '8px',
      }}>
        <div>
          <p style={{ fontSize: '11px', color: COLORS.textMuted, margin: 0 }}>آخر تشغيل</p>
          <p style={{ fontSize: '13px', fontWeight: 500, color: COLORS.text, margin: '2px 0 0' }}>
            {localConfig?.last_run 
              ? new Date(localConfig.last_run).toLocaleString('ar-SA')
              : 'لم يتم التشغيل بعد'
            }
          </p>
        </div>
        
        <button
          onClick={onRun}
          disabled={loading || !localConfig?.enabled}
          style={{
            padding: '8px 16px',
            borderRadius: '8px',
            border: `1px solid ${COLORS.border}`,
            backgroundColor: COLORS.white,
            fontSize: '13px',
            cursor: loading || !localConfig?.enabled ? 'not-allowed' : 'pointer',
            opacity: loading || !localConfig?.enabled ? 0.5 : 1,
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          تشغيل الآن
        </button>
      </div>
      
      {/* Run History */}
      {localConfig?.run_history && localConfig.run_history.length > 0 && (
        <div style={{ marginTop: '16px' }}>
          <p style={{ fontSize: '12px', fontWeight: 500, color: COLORS.textMuted, margin: '0 0 8px' }}>
            سجل التشغيل الأخير
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '120px', overflowY: 'auto' }}>
            {localConfig.run_history.slice().reverse().slice(0, 5).map((run, idx) => (
              <div key={idx} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '8px 10px',
                backgroundColor: COLORS.bg,
                borderRadius: '6px',
                fontSize: '11px',
              }}>
                <span style={{ color: COLORS.textMuted }}>
                  {new Date(run.run_at).toLocaleString('ar-SA', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </span>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <span style={{ color: COLORS.success }}>+{run.approved || 0} معتمد</span>
                  <span style={{ color: COLORS.info }}>+{run.applied || 0} مطبق</span>
                  <span style={{ color: COLORS.primary }}>+{run.rules_created || 0} قاعدة</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN ANALYTICS PAGE
// ═══════════════════════════════════════════════════════════════════════════════

export default function AIAnalyticsPage() {
  const [analytics, setAnalytics] = useState(null);
  const [autoLearningConfig, setAutoLearningConfig] = useState(null);
  const [learningRules, setLearningRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [runningAutoLearn, setRunningAutoLearn] = useState(false);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [analyticsRes, configRes, rulesRes] = await Promise.all([
        axios.get(`${API}/learning/analytics`, { headers: getAuthHeaders() }),
        axios.get(`${API}/learning/auto-learning/status`, { headers: getAuthHeaders() }),
        axios.get(`${API}/learning/rules?limit=20`, { headers: getAuthHeaders() }),
      ]);
      setAnalytics(analyticsRes.data);
      setAutoLearningConfig(configRes.data);
      setLearningRules(rulesRes.data || []);
    } catch (e) {
      console.error('Failed to fetch analytics:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateAutoLearning = async (data) => {
    try {
      const res = await axios.put(`${API}/learning/auto-learning/config`, data, { headers: getAuthHeaders() });
      setAutoLearningConfig(res.data);
    } catch (e) {
      console.error('Failed to update config:', e);
    }
  };

  const handleRunAutoLearning = async () => {
    setRunningAutoLearn(true);
    try {
      await axios.post(`${API}/learning/auto-learning/run`, {}, { headers: getAuthHeaders() });
      await fetchAll();
    } catch (e) {
      console.error('Failed to run auto-learning:', e);
    } finally {
      setRunningAutoLearn(false);
    }
  };

  const handleToggleRule = async (ruleId, active) => {
    try {
      await axios.put(`${API}/learning/rules/${ruleId}/toggle?active=${active}`, {}, { headers: getAuthHeaders() });
      setLearningRules(rules => rules.map(r => r.rule_id === ruleId ? { ...r, active } : r));
    } catch (e) {
      console.error('Failed to toggle rule:', e);
    }
  };

  const handleDeleteRule = async (ruleId) => {
    if (!window.confirm('هل أنت متأكد من حذف هذه القاعدة؟')) return;
    try {
      await axios.delete(`${API}/learning/rules/${ruleId}`, { headers: getAuthHeaders() });
      setLearningRules(rules => rules.filter(r => r.rule_id !== ruleId));
    } catch (e) {
      console.error('Failed to delete rule:', e);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '400px' }}>
        <div style={{ textAlign: 'center', color: COLORS.textMuted }}>
          <RefreshCw size={32} className="animate-spin" style={{ marginBottom: '12px' }} />
          <p>جاري تحميل التحليلات...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Page Header */}
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{
            width: '56px',
            height: '56px',
            borderRadius: '16px',
            backgroundColor: COLORS.primary,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <BarChart3 size={28} color={COLORS.gold} />
          </div>
          <div>
            <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: COLORS.text, margin: 0 }}>
              تحليلات الذكاء الاصطناعي
            </h1>
            <p style={{ fontSize: '14px', color: COLORS.textMuted, margin: '4px 0 0' }}>
              مراقبة وتحسين أداء هلا
            </p>
          </div>
        </div>
        
        <button
          onClick={fetchAll}
          style={{
            padding: '10px 16px',
            borderRadius: '8px',
            border: `1px solid ${COLORS.border}`,
            backgroundColor: COLORS.white,
            fontSize: '13px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          <RefreshCw size={16} />
          تحديث
        </button>
      </div>

      {/* Introduction Banner */}
      <div style={{
        backgroundColor: `${COLORS.primary}08`,
        borderRadius: '16px',
        padding: '20px 24px',
        marginBottom: '24px',
        borderRight: `4px solid ${COLORS.primary}`,
      }}>
        <h3 style={{ margin: '0 0 8px', fontSize: '15px', fontWeight: 600, color: COLORS.primary }}>
          ما هي هذه الصفحة؟
        </h3>
        <p style={{ margin: 0, fontSize: '14px', color: COLORS.textMuted, lineHeight: 1.7 }}>
          لوحة تحليلات شاملة لمراقبة أداء هلا الذكي. تعرض نسبة الدقة في التوصيات، معدل تحويل الجلسات، 
          أكثر الأخطاء شيوعاً، وتمكّنك من إدارة <strong>التعلم التلقائي</strong> الذي يحسّن النظام 
          باستمرار من خلال التقييمات المعتمدة.
        </p>
      </div>

      {/* Overview Stats */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', 
        gap: '16px',
        marginBottom: '24px'
      }}>
        <StatCard
          title="دقة النظام"
          value={`${analytics?.overview?.accuracy_rate || 0}%`}
          subtitle="نسبة التقييمات الصحيحة"
          icon={Target}
          color={analytics?.overview?.accuracy_rate >= 80 ? COLORS.success : analytics?.overview?.accuracy_rate >= 50 ? COLORS.warning : COLORS.error}
          trend={analytics?.overview?.accuracy_rate >= 80 ? 'up' : analytics?.overview?.accuracy_rate >= 50 ? 'neutral' : 'down'}
          trendValue={analytics?.overview?.accuracy_rate >= 80 ? 'ممتاز' : analytics?.overview?.accuracy_rate >= 50 ? 'مقبول' : 'يحتاج تحسين'}
        />
        
        <StatCard
          title="معدل التحويل"
          value={`${analytics?.overview?.conversion_rate || 0}%`}
          subtitle="الجلسات التي وصلت لخطة"
          icon={TrendingUp}
          color={COLORS.info}
        />
        
        <StatCard
          title="إجمالي الجلسات"
          value={analytics?.overview?.total_sessions || 0}
          subtitle={`${analytics?.overview?.total_messages || 0} رسالة`}
          icon={Activity}
          color={COLORS.primary}
        />
        
        <StatCard
          title="تحتاج مراجعة"
          value={analytics?.sessions_needing_review || 0}
          subtitle="جلسات بها أخطاء معلقة"
          icon={AlertTriangle}
          color={COLORS.warning}
        />
      </div>

      {/* Two Column Layout */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
        {/* Feedback Breakdown */}
        <div style={{
          backgroundColor: COLORS.white,
          borderRadius: '16px',
          padding: '24px',
          border: `1px solid ${COLORS.border}`,
        }}>
          <h3 style={{ margin: '0 0 20px', fontSize: '16px', fontWeight: 600, color: COLORS.text }}>
            توزيع التقييمات
          </h3>
          
          <ProgressBar
            label="صحيحة"
            value={analytics?.feedback_breakdown?.correct || 0}
            total={analytics?.overview?.total_feedbacks || 1}
            color={COLORS.success}
          />
          <ProgressBar
            label="مقبولة"
            value={analytics?.feedback_breakdown?.partial || 0}
            total={analytics?.overview?.total_feedbacks || 1}
            color={COLORS.warning}
          />
          <ProgressBar
            label="خاطئة"
            value={analytics?.feedback_breakdown?.wrong || 0}
            total={analytics?.overview?.total_feedbacks || 1}
            color={COLORS.error}
          />
          
          <div style={{
            marginTop: '20px',
            padding: '16px',
            backgroundColor: COLORS.bg,
            borderRadius: '12px',
            display: 'flex',
            justifyContent: 'space-around',
          }}>
            <div style={{ textAlign: 'center' }}>
              <p style={{ fontSize: '24px', fontWeight: 700, color: COLORS.success, margin: 0 }}>
                {analytics?.feedback_breakdown?.correct_percentage || 0}%
              </p>
              <p style={{ fontSize: '11px', color: COLORS.textMuted, margin: '4px 0 0' }}>صحيحة</p>
            </div>
            <div style={{ textAlign: 'center' }}>
              <p style={{ fontSize: '24px', fontWeight: 700, color: COLORS.warning, margin: 0 }}>
                {analytics?.feedback_breakdown?.partial_percentage || 0}%
              </p>
              <p style={{ fontSize: '11px', color: COLORS.textMuted, margin: '4px 0 0' }}>مقبولة</p>
            </div>
            <div style={{ textAlign: 'center' }}>
              <p style={{ fontSize: '24px', fontWeight: 700, color: COLORS.error, margin: 0 }}>
                {analytics?.feedback_breakdown?.wrong_percentage || 0}%
              </p>
              <p style={{ fontSize: '11px', color: COLORS.textMuted, margin: '4px 0 0' }}>خاطئة</p>
            </div>
          </div>
        </div>

        {/* Top Errors */}
        <div style={{
          backgroundColor: COLORS.white,
          borderRadius: '16px',
          padding: '24px',
          border: `1px solid ${COLORS.border}`,
        }}>
          <h3 style={{ margin: '0 0 20px', fontSize: '16px', fontWeight: 600, color: COLORS.text }}>
            أكثر الأخطاء شيوعاً
          </h3>
          
          {analytics?.top_errors?.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {analytics.top_errors.map((err, idx) => (
                <div key={idx} style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px',
                  backgroundColor: idx === 0 ? COLORS.errorLight : COLORS.bg,
                  borderRadius: '8px',
                  borderRight: idx === 0 ? `4px solid ${COLORS.error}` : 'none',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '6px',
                      backgroundColor: idx === 0 ? COLORS.error : COLORS.textMuted,
                      color: COLORS.white,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      fontWeight: 600,
                    }}>
                      {idx + 1}
                    </span>
                    <span style={{ fontSize: '13px', color: COLORS.text }}>
                      {ERROR_REASON_LABELS[err.reason] || err.reason}
                    </span>
                  </div>
                  <span style={{
                    padding: '4px 10px',
                    borderRadius: '6px',
                    backgroundColor: idx === 0 ? COLORS.error : COLORS.bgDark,
                    color: idx === 0 ? COLORS.white : COLORS.text,
                    fontSize: '13px',
                    fontWeight: 600,
                  }}>
                    {err.count}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px', color: COLORS.textMuted }}>
              <CheckCircle size={40} style={{ marginBottom: '12px', opacity: 0.5 }} />
              <p>لا توجد أخطاء مسجلة</p>
            </div>
          )}
        </div>
      </div>

      {/* ══ REAL ANALYTICS: Conversion Funnel + City Distribution ══ */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
        {/* Conversion Funnel */}
        <div style={{
          backgroundColor: COLORS.white, borderRadius: '16px',
          padding: '24px', border: `1px solid ${COLORS.border}`,
        }}>
          <h3 style={{ margin: '0 0 20px', fontSize: '16px', fontWeight: 600, color: COLORS.text }}>
            قمع التحويل
          </h3>
          {(analytics?.conversion_funnel || []).map((step, idx, arr) => {
            const maxCount = arr[0]?.count || 1;
            const pct = maxCount > 0 ? Math.round(step.count / maxCount * 100) : 0;
            return (
              <div key={idx} style={{ marginBottom: '14px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <span style={{ fontSize: '13px', color: COLORS.text }}>{step.stage}</span>
                  <span style={{ fontSize: '13px', fontWeight: 600, color: COLORS.text }}>{step.count}</span>
                </div>
                <div style={{ height: '8px', borderRadius: '4px', backgroundColor: COLORS.bg }}>
                  <div style={{
                    height: '100%', borderRadius: '4px', width: `${pct}%`,
                    backgroundColor: idx === 0 ? COLORS.primary : idx === arr.length - 1 ? COLORS.success : COLORS.info,
                    transition: 'width 0.5s ease',
                  }} />
                </div>
                {idx > 0 && arr[idx-1].count > 0 && (
                  <p style={{ margin: '2px 0 0', fontSize: '11px', color: COLORS.textMuted }}>
                    {Math.round(step.count / arr[idx-1].count * 100)}% من المرحلة السابقة
                  </p>
                )}
              </div>
            );
          })}
        </div>

        {/* Support Type + City Distribution */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* Support Type Distribution */}
          <div style={{
            backgroundColor: COLORS.white, borderRadius: '16px',
            padding: '24px', border: `1px solid ${COLORS.border}`, flex: 1,
          }}>
            <h3 style={{ margin: '0 0 16px', fontSize: '16px', fontWeight: 600, color: COLORS.text }}>
              أنواع الدعم المطلوبة
            </h3>
            {(analytics?.top_support_types || []).length > 0 ? (
              (analytics.top_support_types).slice(0, 5).map((item, idx) => {
                const maxC = analytics.top_support_types[0]?.count || 1;
                return (
                  <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                    <span style={{ fontSize: '12px', color: COLORS.text, minWidth: '120px', textAlign: 'right' }}>
                      {(item.type || '').slice(0, 25)}
                    </span>
                    <div style={{ flex: 1, height: '6px', borderRadius: '3px', backgroundColor: COLORS.bg }}>
                      <div style={{
                        height: '100%', borderRadius: '3px',
                        width: `${Math.round(item.count / maxC * 100)}%`,
                        backgroundColor: [COLORS.primary, COLORS.info, COLORS.success, COLORS.warning, COLORS.gold][idx % 5],
                      }} />
                    </div>
                    <span style={{ fontSize: '12px', fontWeight: 600, color: COLORS.textMuted, minWidth: '30px' }}>{item.count}</span>
                  </div>
                );
              })
            ) : <p style={{ color: COLORS.textMuted, fontSize: '13px' }}>لا توجد بيانات</p>}
          </div>

          {/* Top Cities */}
          <div style={{
            backgroundColor: COLORS.white, borderRadius: '16px',
            padding: '24px', border: `1px solid ${COLORS.border}`, flex: 1,
          }}>
            <h3 style={{ margin: '0 0 16px', fontSize: '16px', fontWeight: 600, color: COLORS.text }}>
              أكثر المدن طلباً
            </h3>
            {(analytics?.top_cities || []).length > 0 ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {analytics.top_cities.slice(0, 8).map((city, idx) => (
                  <span key={idx} style={{
                    padding: '6px 14px', borderRadius: '20px',
                    backgroundColor: idx === 0 ? `${COLORS.primary}15` : COLORS.bg,
                    border: idx === 0 ? `1px solid ${COLORS.primary}40` : `1px solid ${COLORS.border}`,
                    fontSize: '13px', color: idx === 0 ? COLORS.primary : COLORS.text,
                    fontWeight: idx === 0 ? 600 : 400,
                  }}>
                    {city.city} ({city.count})
                  </span>
                ))}
              </div>
            ) : <p style={{ color: COLORS.textMuted, fontSize: '13px' }}>لا توجد بيانات</p>}
          </div>
        </div>
      </div>

      {/* Auto Learning Panel */}
      <div style={{ marginBottom: '24px' }}>
        <AutoLearningPanel
          config={autoLearningConfig}
          onUpdate={handleUpdateAutoLearning}
          onRun={handleRunAutoLearning}
          loading={runningAutoLearn}
        />
      </div>

      {/* Learning Rules & Failing Intents */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '24px' }}>
        {/* Learning Rules */}
        <div style={{
          backgroundColor: COLORS.white,
          borderRadius: '16px',
          padding: '24px',
          border: `1px solid ${COLORS.border}`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: COLORS.text }}>
              قواعد التعلم
            </h3>
            <div style={{ display: 'flex', gap: '8px' }}>
              <span style={{
                padding: '4px 10px',
                borderRadius: '6px',
                backgroundColor: COLORS.successLight,
                color: COLORS.success,
                fontSize: '12px',
              }}>
                {analytics?.learning_rules?.active || 0} نشطة
              </span>
              <span style={{
                padding: '4px 10px',
                borderRadius: '6px',
                backgroundColor: COLORS.infoLight,
                color: COLORS.info,
                fontSize: '12px',
              }}>
                {analytics?.learning_rules?.auto_generated || 0} تلقائية
              </span>
            </div>
          </div>
          
          {learningRules.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '300px', overflowY: 'auto' }}>
              {learningRules.map(rule => (
                <div key={rule.rule_id} style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px',
                  backgroundColor: COLORS.bg,
                  borderRadius: '8px',
                  opacity: rule.active ? 1 : 0.6,
                }}>
                  <div style={{ flex: 1 }}>
                    <p style={{ 
                      margin: 0, 
                      fontSize: '13px', 
                      color: COLORS.text,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: '300px',
                    }}>
                      {rule.input_pattern || rule.expected_intent || rule.rule_id}
                    </p>
                    <p style={{ margin: '2px 0 0', fontSize: '11px', color: COLORS.textMuted }}>
                      {rule.rule_type} • {rule.auto_generated ? 'تلقائي' : 'يدوي'}
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button
                      onClick={() => handleToggleRule(rule.rule_id, !rule.active)}
                      style={{
                        padding: '6px 10px',
                        borderRadius: '6px',
                        border: 'none',
                        backgroundColor: rule.active ? COLORS.successLight : COLORS.bgDark,
                        color: rule.active ? COLORS.success : COLORS.textMuted,
                        fontSize: '11px',
                        cursor: 'pointer',
                      }}
                    >
                      {rule.active ? 'نشطة' : 'معطلة'}
                    </button>
                    <button
                      onClick={() => handleDeleteRule(rule.rule_id)}
                      style={{
                        padding: '6px',
                        borderRadius: '6px',
                        border: 'none',
                        backgroundColor: COLORS.errorLight,
                        color: COLORS.error,
                        cursor: 'pointer',
                      }}
                    >
                      <XCircle size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px', color: COLORS.textMuted }}>
              <Brain size={40} style={{ marginBottom: '12px', opacity: 0.5 }} />
              <p>لا توجد قواعد تعلم بعد</p>
            </div>
          )}
        </div>

        {/* Top Failing Intents */}
        <div style={{
          backgroundColor: COLORS.white,
          borderRadius: '16px',
          padding: '24px',
          border: `1px solid ${COLORS.border}`,
        }}>
          <h3 style={{ margin: '0 0 20px', fontSize: '16px', fontWeight: 600, color: COLORS.text }}>
            Intents الأكثر فشلاً
          </h3>
          
          {analytics?.top_failing_intents?.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {analytics.top_failing_intents.map((intent, idx) => (
                <div key={idx} style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '10px 12px',
                  backgroundColor: COLORS.bg,
                  borderRadius: '8px',
                }}>
                  <span style={{ 
                    fontSize: '12px', 
                    color: COLORS.text,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '150px',
                  }}>
                    {intent.intent}
                  </span>
                  <span style={{
                    padding: '2px 8px',
                    borderRadius: '4px',
                    backgroundColor: COLORS.errorLight,
                    color: COLORS.error,
                    fontSize: '12px',
                    fontWeight: 600,
                  }}>
                    {intent.count}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '30px', color: COLORS.textMuted }}>
              <CheckCircle size={32} style={{ marginBottom: '8px', opacity: 0.5 }} />
              <p style={{ fontSize: '13px' }}>لا توجد intents فاشلة</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
